import { CFDFile } from '@aero-platform/shared'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
import { FC, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSimTemplateUpload } from '../../hooks/useSimTemplateUpload'
import Modal from '../Modal'
import SimTemplateDropdown from './SimTemplateDropdown'
import SimUploadStatus from './SimUploadStatus'
import { hasSimTemplateChanged } from '../../utils/simTemplateOverride'

type ResubmitCaseModalProps = {
  isOpen: boolean
  caseName: string
  submissionId?: string
  submissionName?: string
  currentSimTemplate?: CFDFile | null
  isLoading: boolean
  onConfirm: (data: {
    simTemplate: CFDFile | null
    uploadedFile: File | null
    simChangeReason?: string
  }) => Promise<void>
  onCancel: () => void
  /** Called once a freshly uploaded .sim has finished uploading, so the caller can persist it. */
  onSimUploaded?: (template: CFDFile) => void | Promise<void>
}

/**
 * Modal for resubmitting a case. The user may keep the current SIM template or replace it
 * (select an existing template or upload a new .sim) the same way as editing a case; a
 * change reason is required when the template differs from the current one.
 */
const ResubmitCaseModal: FC<ResubmitCaseModalProps> = ({
  isOpen,
  caseName,
  submissionId,
  submissionName,
  currentSimTemplate,
  isLoading,
  onConfirm,
  onCancel,
  onSimUploaded
}) => {
  const simFileInputRef = useRef<HTMLInputElement>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<CFDFile | null>(
    currentSimTemplate ?? null
  )
  const sim = useSimTemplateUpload()
  const [reason, setReason] = useState('')
  const [reasonTouched, setReasonTouched] = useState(false)

  // The modal stays mounted while isOpen toggles, so re-seed state from the case being
  // resubmitted every time it opens — otherwise the initial useState value (captured when
  // no case was selected) sticks and the dropdown auto-selects another case's template.
  useEffect(() => {
    if (isOpen) {
      setSelectedTemplate(currentSimTemplate ?? null)
      setReason('')
      setReasonTouched(false)
      sim.reset()
    }
    // sim.reset is stable; intentionally excluded to only re-seed on open/case change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentSimTemplate])

  // Compare FULL identity (file + blob path), not just filename, so a deliberate dropdown pick
  // or upload that happens to share a filename still counts as a change and overwrites the case.
  const isChanged = hasSimTemplateChanged(currentSimTemplate, selectedTemplate)
  const isReasonValid = reason.trim().length > 0

  // Per-case .sim upload: direct-to-blob (files can be multi-GB) into a permanent sim-templates
  // folder. We now await the transfer AND verify the blob is present in Azure before the
  // template is attached — so the user can never resubmit against a sim that never landed.
  const handleSimTemplateUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (simFileInputRef.current) simFileInputRef.current.value = ''
    if (!file) return
    const landedTemplate = await sim.startUpload(file, { runName: submissionName || file.name })
    if (landedTemplate) {
      setSelectedTemplate(landedTemplate)
      // Persist onto the case only after the blob upload is confirmed in Azure, so the uploaded
      // template survives a page refresh (the sim-templates list is rebuilt from the DB).
      if (onSimUploaded) {
        try {
          await onSimUploaded(landedTemplate)
        } catch {
          /* persistence failure is surfaced by the caller */
        }
      }
      toast.success('SIM file confirmed in Azure.')
    }
  }

  // Block confirm while a fresh upload is in flight or if it failed to land in Azure.
  const freshUploadBlocked =
    sim.phase === 'uploading' || sim.phase === 'verifying' || sim.phase === 'failed'

  const handleConfirm = async () => {
    if (freshUploadBlocked) {
      toast.error('Wait for the SIM upload to be confirmed in Azure before resubmitting.')
      return
    }
    if (isChanged && !isReasonValid) {
      setReasonTouched(true)
      toast.error('A reason is required for the SIM template change.')
      return
    }
    await onConfirm({
      simTemplate: isChanged ? selectedTemplate : null,
      uploadedFile: null,
      simChangeReason: isChanged ? reason.trim() : undefined
    })
  }

  // Cancelling the modal aborts any in-flight upload so it doesn't keep running in the background.
  const handleCancel = () => {
    sim.cancel()
    onCancel()
  }

  return (
    <Modal
      isOpen={isOpen}
      title={`Resubmit Case: ${caseName}`}
      withAction
      showCancelButton
      confirmButtonCaption="Resubmit"
      cancelButtonCaption="Cancel"
      confirmButtonDisabled={
        isLoading || sim.isBusy || freshUploadBlocked || (isChanged && !isReasonValid)
      }
      cancelButtonDisabled={isLoading}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      size="md">
      <div className="py-4 space-y-4">
        <div className="bg-blue-50 dark:bg-neutral-80 border border-blue-200 dark:border-neutral-70 rounded-md p-4">
          <p className="text-sm text-gray-700 dark:text-neutral-20">
            Resubmit case <strong>{caseName}</strong> for processing.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-neutral-10 mb-2">
            SIM Template
          </label>
          <SimTemplateDropdown
            submissionId={submissionId}
            submissionName={submissionName}
            value={selectedTemplate}
            onChange={v => {
              setSelectedTemplate(v)
              sim.reset()
            }}
            placeholder={'Select template or search caseId'}
            autoSelectFirstOption={false}
            disabled={isLoading || sim.isBusy}
          />
          <div className="text-sm text-gray-500 mt-1">
            {selectedTemplate
              ? `(${selectedTemplate.rootType}-${
                  selectedTemplate.fileType || selectedTemplate.rootType || selectedTemplate.system
                }) ${selectedTemplate.file}`
              : 'None'}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              ref={simFileInputRef}
              type="file"
              accept=".sim"
              onChange={handleSimTemplateUpload}
              className="hidden"
            />
            <Button
              type="button"
              label={sim.isBusy ? 'Uploading…' : 'Upload .sim'}
              icon="pi pi-upload pr-1"
              size="small"
              outlined
              disabled={isLoading || sim.isBusy}
              onClick={() => simFileInputRef.current?.click()}
            />
          </div>
          <SimUploadStatus
            phase={sim.phase}
            progress={sim.progress}
            error={sim.error}
            fileName={sim.uploadedTemplate?.file}
          />
        </div>

        {isChanged && (
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-neutral-10 mb-2">
              Reason for SIM change <span className="text-red-500">*</span>
            </label>
            <InputTextarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              onBlur={() => setReasonTouched(true)}
              rows={3}
              className="w-full"
              placeholder="Describe why the SIM template is changing…"
            />
            {reasonTouched && !isReasonValid && (
              <small className="error">A reason is required for the SIM template change.</small>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ResubmitCaseModal
